import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Sun, Users, Plus, Ban, GripVertical, UserRound } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');
const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const AdminMonthCalendar = ({ appointments, language, onCreate, onMove, onCancel, onProfile }) => {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const pt = language === 'pt';
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selected, setSelected] = useState(null);
  const todayKey = key(new Date());

  const byDay = useMemo(() => {
    const m = {};
    appointments.forEach(a => {
      if (a.status === 'cancelled') return;
      const d = a.preferred_date || a.date;
      if (!d) return;
      (m[d] = m[d] || []).push(a);
    });
    Object.values(m).forEach(list => list.sort((x, y) => (x.preferred_time || x.time || '').localeCompare(y.preferred_time || y.time || '')));
    return m;
  }, [appointments]);

  const days = useMemo(() => {
    const y = cursor.getFullYear(), mo = cursor.getMonth();
    const first = new Date(y, mo, 1);
    const offset = (first.getDay() + 6) % 7;
    const total = new Date(y, mo + 1, 0).getDate();
    const cells = Array(offset).fill(null);
    for (let i = 1; i <= total; i++) cells.push(new Date(y, mo, i));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [cursor]);

  const monthKeys = days.filter(Boolean).map(key);
  const freeDays = monthKeys.filter(k => !byDay[k] && k >= todayKey).length;
  const busyDays = monthKeys.filter(k => byDay[k]).length;
  const dayNames = pt ? ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const selectedList = selected ? (byDay[selected] || []) : [];

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-month-calendar">
      <div className="bg-primary text-white p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="month-prev"><ChevronLeft size={20} /></button>
          <h3 className="font-semibold text-lg capitalize min-w-[170px] text-center" data-testid="month-label">{cursor.toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { month: 'long', year: 'numeric' })}</h3>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="month-next"><ChevronRight size={20} /></button>
          <button onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); setSelected(todayKey); }} className="ml-2 text-xs px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25" data-testid="month-today">{pt ? 'Hoje' : 'Today'}</button>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1" data-testid="month-free-count"><Sun size={14} className="text-emerald-300" /> <b>{freeDays}</b> {pt ? 'dias livres' : 'free days'}</span>
          <span className="inline-flex items-center gap-1" data-testid="month-busy-count"><Users size={14} className="text-amber-300" /> <b>{busyDays}</b> {pt ? 'dias com serviços' : 'days with jobs'}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 px-4 py-2 text-xs text-gray-600 border-b bg-gray-50">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300"></span> {pt ? 'Livre' : 'Free'}</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300"></span> 1–2 {pt ? 'serviços' : 'jobs'}</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-100 border border-red-300"></span> 3+ {pt ? 'serviços' : 'jobs'}</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-500"></span> Airbnb</span>
        {onMove && <span className="inline-flex items-center gap-1 text-gray-500 ml-auto"><GripVertical size={12} /> {pt ? 'Arraste um serviço para outro dia para reagendar' : 'Drag a job to another day to reschedule'}</span>}
      </div>
      <div className="grid grid-cols-7 border-b">
        {dayNames.map(d => <div key={d} className="p-2 text-center text-xs font-medium text-gray-500 bg-gray-50 border-r last:border-r-0">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="min-h-[90px] border-b border-r bg-gray-50/60" />;
          const k = key(d);
          const list = byDay[k] || [];
          const past = k < todayKey;
          const tone = list.length === 0 ? (past ? 'bg-white' : 'bg-emerald-50 hover:bg-emerald-100') : list.length <= 2 ? 'bg-amber-50 hover:bg-amber-100' : 'bg-red-50 hover:bg-red-100';
          return (
            <div key={k} role="button" tabIndex={0} onClick={() => setSelected(k)} data-testid={`month-day-${k}`}
              onDragOver={(e) => { if (dragId && !past) { e.preventDefault(); setDragOver(k); } }}
              onDragLeave={() => setDragOver(prev => (prev === k ? null : prev))}
              onDrop={(e) => { e.preventDefault(); if (dragId && !past && onMove) { const a = appointments.find(x => x.id === dragId); if (a && (a.preferred_date || a.date) !== k) onMove(a, k); } setDragId(null); setDragOver(null); }}
              className={`min-h-[90px] p-1.5 border-b border-r text-left transition-colors relative cursor-pointer ${tone} ${selected === k ? 'ring-2 ring-inset ring-primary' : ''} ${past ? 'opacity-60' : ''} ${dragOver === k ? 'ring-2 ring-inset ring-blue-500 bg-blue-50' : ''}`}>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${k === todayKey ? 'bg-primary text-white' : 'text-gray-700'}`}>{d.getDate()}</span>
                {list.length === 0 && !past && (
                  <span className="text-[10px] font-semibold text-emerald-700 uppercase inline-flex items-center gap-1">{pt ? 'Livre' : 'Free'}
                    {onCreate && <span role="button" onClick={(e) => { e.stopPropagation(); onCreate(k); }} data-testid={`month-day-quick-create-${k}`} className="w-4 h-4 rounded-full bg-emerald-600 text-white inline-flex items-center justify-center hover:bg-emerald-700" title={pt ? 'Novo agendamento' : 'New appointment'}><Plus size={10} /></span>}
                  </span>
                )}
                {list.length > 0 && <span className="text-[10px] font-semibold text-gray-600">{list.length}</span>}
              </div>
              <div className="mt-1 space-y-0.5">
                {list.slice(0, 2).map(a => {
                  const movable = onMove && a.status !== 'completed' && a.status !== 'cancelled';
                  return (
                    <div key={a.id} draggable={movable} data-testid={`month-appt-${a.id}`}
                      onDragStart={(e) => { if (!movable) return; setDragId(a.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDragId(null); setDragOver(null); }}
                      className={`text-[10px] leading-tight truncate px-1 py-0.5 rounded flex items-center gap-0.5 ${movable ? 'cursor-grab active:cursor-grabbing' : ''} ${dragId === a.id ? 'opacity-40' : ''} ${a.source === 'airbnb' ? 'bg-rose-500 text-white' : a.status === 'completed' ? 'bg-green-200 text-green-900' : 'bg-white/80 text-gray-700 border'}`}>
                      {movable && <GripVertical size={9} className="flex-shrink-0 opacity-60" />}
                      <span className="truncate">{(a.preferred_time || a.time || '').slice(0, 5)} {a.name}</span>
                    </div>
                  );
                })}
                {list.length > 2 && <div className="text-[10px] text-gray-500 pl-1">+{list.length - 2}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {selected && (
        <div className="border-t p-4 bg-gray-50" data-testid="month-day-details">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 capitalize">{new Date(selected + 'T00:00:00').toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { weekday: 'long', day: 'numeric', month: 'long' })}</h4>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${selectedList.length === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {selectedList.length === 0 ? (pt ? 'Dia livre — disponível para clientes' : 'Free day — available for clients') : `${selectedList.length} ${pt ? 'serviço(s)' : 'job(s)'}`}
              </span>
              {onCreate && selected >= todayKey && (
                <button onClick={() => onCreate(selected)} data-testid="month-day-create"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                  <Plus size={14} /> {pt ? 'Novo agendamento neste dia' : 'New appointment on this day'}
                </button>
              )}
            </div>
          </div>
          {selectedList.length > 0 && (
            <div className="space-y-2">
              {selectedList.map(a => (
                <div key={a.id} draggable={onMove && a.status !== 'completed' && a.status !== 'cancelled'} data-testid={`month-detail-appt-${a.id}`}
                  onDragStart={(e) => { setDragId(a.id); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragId(null); setDragOver(null); }}
                  className={`p-3 rounded-lg border-l-4 bg-white ${onMove && a.status !== 'completed' ? 'cursor-grab' : ''} ${a.source === 'airbnb' ? 'border-rose-500' : a.status === 'completed' ? 'border-green-500' : a.status === 'in_progress' ? 'border-blue-500' : 'border-gray-300'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {onMove && a.status !== 'completed' && <GripVertical size={16} className="text-gray-400 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{(a.preferred_time || a.time || '--:--')} · {a.name} {a.source === 'airbnb' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white uppercase">Airbnb</span>}</p>
                        <p className="text-xs text-gray-600 truncate">{a.service_type} · {a.address || a.eircode || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-500 hidden sm:inline">{(a.assigned_staff_names && a.assigned_staff_names.join(', ')) || a.assigned_staff_name || (pt ? 'Sem funcionário' : 'Unassigned')}</span>
                      {onProfile && (
                        <button onClick={() => onProfile(a)} data-testid={`month-profile-${a.id}`} title={pt ? 'Ficha do cliente' : 'Client profile'}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"><UserRound size={12} /> {pt ? 'Ficha' : 'Profile'}</button>
                      )}
                      {onCancel && a.status !== 'completed' && a.status !== 'cancelled' && (
                        <button onClick={() => onCancel(a)} data-testid={`month-cancel-${a.id}`} title={pt ? 'Cancelar serviço' : 'Cancel job'}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Ban size={12} /> {pt ? 'Cancelar' : 'Cancel'}</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMonthCalendar;
